import React, { useState, useRef, useEffect } from 'react';
import { FaTimes, FaChevronDown, FaPlus } from 'react-icons/fa';

const MultiSelectInput = ({
  label,
  placeholder,
  values = [],
  onChange,
  suggestions = [],
  allowCustom = true,
  className = '',
  renderOption,
}) => {
  const displayOf = (value) =>
    typeof renderOption === 'function' ? renderOption(value) : value;
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState(suggestions);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setInputValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (inputValue) {
      const needle = inputValue.toLowerCase();
      const filtered = suggestions.filter(
        (suggestion) =>
          (suggestion.toLowerCase().includes(needle) ||
            String(displayOf(suggestion)).toLowerCase().includes(needle)) &&
          !values.includes(suggestion),
      );
      setFilteredSuggestions(filtered);
    } else {
      setFilteredSuggestions(
        suggestions.filter((suggestion) => !values.includes(suggestion)),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, suggestions, values]);

  const addValue = (value) => {
    if (value && !values.includes(value)) {
      onChange([...values, value]);
      setInputValue('');
      setIsOpen(false);
    }
  };

  const removeValue = (valueToRemove) => {
    onChange(values.filter((value) => value !== valueToRemove));
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim() && allowCustom) {
      e.preventDefault();
      addValue(inputValue.trim());
    } else if (e.key === 'Backspace' && !inputValue && values.length > 0) {
      removeValue(values[values.length - 1]);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>

      <div className="relative">
        <div
          className={`min-h-[42px] w-full px-3 py-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 cursor-text bg-white ${
            isOpen ? 'ring-2 ring-indigo-500 border-indigo-500' : ''
          }`}
          onClick={() => {
            setIsOpen(true);
            inputRef.current?.focus();
          }}
        >
          <div className="flex flex-wrap gap-2 items-center">
            {/* Selected values */}
            {values.map((value, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded-md text-sm"
              >
                {displayOf(value)}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeValue(value);
                  }}
                  className="hover:text-indigo-600 focus:outline-none"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </span>
            ))}

            {/* Input */}
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onFocus={() => setIsOpen(true)}
              placeholder={values.length === 0 ? placeholder : ''}
              className="flex-1 min-w-[120px] outline-none bg-transparent"
            />
          </div>

          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <FaChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            {inputValue &&
              allowCustom &&
              !values.includes(inputValue.trim()) && (
                <div
                  onClick={() => addValue(inputValue.trim())}
                  className="px-3 py-2 cursor-pointer hover:bg-indigo-50 border-b border-gray-100 flex items-center gap-2 text-indigo-600"
                >
                  <FaPlus className="w-3 h-3" />
                  Adaugă "{inputValue.trim()}"
                </div>
              )}

            {filteredSuggestions.length > 0
              ? filteredSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    onClick={() => addValue(suggestion)}
                    className="px-3 py-2 cursor-pointer hover:bg-gray-50 text-gray-700"
                  >
                    {displayOf(suggestion)}
                  </div>
                ))
              : !inputValue && (
                  <div className="px-3 py-2 text-gray-500 text-sm">
                    {allowCustom
                      ? 'Scrie pentru a adăuga valori personalizate'
                      : 'Nu există opțiuni disponibile'}
                  </div>
                )}
          </div>
        )}
      </div>

      {values.length > 0 && (
        <div className="mt-1 text-xs text-gray-500">
          {values.length} element{values.length !== 1 ? 'e' : ''} selectat
          {values.length !== 1 ? 'e' : ''}
        </div>
      )}
    </div>
  );
};

export default MultiSelectInput;
